import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LangProvider } from "@/context/LangContext";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SupportWidget from "@/components/SupportWidget";
import Home from "@/pages/Home";
import Directory from "@/pages/Directory";
import BusinessProfile from "@/pages/BusinessProfile";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import BusinessSettings from "@/pages/dashboard/BusinessSettings";
import BusinessSite from "@/pages/dashboard/BusinessSite";
import Leads from "@/pages/dashboard/Leads";
import { FeatureGate } from "@/components/FeatureGate";
import Gallery from "@/pages/dashboard/Gallery";
import Clients from "@/pages/dashboard/Clients";
import Invoices from "@/pages/dashboard/Invoices";
import Projects from "@/pages/dashboard/Projects";
import Profitability from "@/pages/dashboard/Profitability";
import Timesheets from "@/pages/dashboard/Timesheets";
import Payroll from "@/pages/dashboard/Payroll";
import Accounting from "@/pages/dashboard/Accounting";
import Receivables from "@/pages/dashboard/Receivables";
import ProjectProgress from "@/pages/ProjectProgress";
import Employees from "@/pages/dashboard/Employees";
import Subscription from "@/pages/dashboard/Subscription";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import Notifications from "@/pages/dashboard/Notifications";
import Messages from "@/pages/dashboard/Messages";
import AuthCallback from "@/pages/AuthCallback";
import ClientChat from "@/pages/ClientChat";
import Onboarding from "@/pages/Onboarding";
import PublicInvoice from "@/pages/PublicInvoice";
import Support from "@/pages/Support";
import Manual from "@/pages/Manual";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import Legal from "@/pages/legal/Legal";

function PublicLayout({ children }) {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function RootRoutes() {
  const location = useLocation();
  // Handle Google OAuth callback synchronously before any protected route runs
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
      <Route path="/directory" element={<PublicLayout><Directory /></PublicLayout>} />
      <Route path="/business/:id" element={<PublicLayout><BusinessProfile /></PublicLayout>} />
      <Route path="/pricing" element={<PublicLayout><Pricing /></PublicLayout>} />
      <Route path="/support" element={<PublicLayout><Support /></PublicLayout>} />
      <Route path="/manual" element={<PublicLayout><Manual /></PublicLayout>} />
      <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />
      <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
      <Route path="/legal" element={<PublicLayout><Legal /></PublicLayout>} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/p/:token" element={<ProjectProgress />} />
      <Route path="/pay/:token" element={<PublicInvoice />} />
      <Route path="/chat/:sid" element={<ClientChat />} />

      <Route path="/dashboard" element={<ProtectedRoute roles={["business", "admin"]}><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Overview />} />
        <Route path="business" element={<BusinessSettings />} />
        <Route path="site" element={<FeatureGate feature="business_site"><BusinessSite /></FeatureGate>} />
        <Route path="leads" element={<FeatureGate feature="business_site"><Leads /></FeatureGate>} />
        <Route path="gallery" element={<Gallery />} />
        <Route path="clients" element={<Clients />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="receivables" element={<Receivables />} />
        <Route path="projects" element={<Projects />} />
        <Route path="profitability" element={<Profitability />} />
        <Route path="timesheets" element={<Timesheets />} />
        <Route path="payroll" element={<Payroll />} />
        <Route path="accounting" element={<Accounting />} />
        <Route path="employees" element={<Employees />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="messages" element={<Messages />} />
      </Route>

      <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><PublicLayout><AdminDashboard /></PublicLayout></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute roles={["admin"]}><PublicLayout><AdminSettings /></PublicLayout></ProtectedRoute>} />
      <Route path="/admin/notifications" element={<ProtectedRoute roles={["admin"]}><PublicLayout><AdminNotifications /></PublicLayout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LangProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <RootRoutes />
            <SupportWidget />
          </BrowserRouter>
        </AuthProvider>
      </LangProvider>
    </div>
  );
}

export default App;
